import $ from 'jquery'
import { Util } from 'bootstrap'

import Confirm from './confirm'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'formsync'
const DATA_KEY = `opp.${NAME}`
const EVENT_KEY = '.' + DATA_KEY
const DATA_API_KEY = '.data-api'
const JQUERY_NO_CONFLICT = $.fn[NAME]

const Default = {
  delay: 500,
  modal: '',
  notify: '',
  confirm: ''
}

const DefaultType = {
  delay: 'number',
  modal: '(string|element)',
  notify: '(boolean|string|element)',
  confirm: '(boolean|string|element)'
}

const ClassName = {
  LOCKED: 'disabled'
}

const Selector = {
  FORM_NOTIFY: '.form-notify > *',
  FORM_CONFIRM: '.form-confirm > *',
  DATA_FORM_SYNC: 'form[data-form="sync"]'
}

const Event = {
  SUBMIT: `submit${EVENT_KEY}`,
  PAGESHOW: `pageshow${EVENT_KEY}`,
  CLICK_LOCKED: `click.disabled${EVENT_KEY}`,
  SUBMIT_DATA_API: `submit${EVENT_KEY}${DATA_API_KEY}`
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class FormSync {
  constructor(element, config) {
    this._element = element
    this._config = this._getConfig(config)
    this._modalElement = this._config.modal ? this._getModalElement() : null
    this._notifyElement = this._config.notify ? this._getNotifyElement() : null
    this._confirmElements = this._config.confirm ? this._getConfirmElements() : null
    this._locked = false
    this._submitted = false

    $(element).on(Event.SUBMIT, event => this.submit(event))

    // Refresh form if page is loaded from cache
    $(window).on(Event.PAGESHOW, event => {
      if (event.persisted) {
        this._submitted = false
        this._unlock()
      }
    })
  }

  // Public

  submit(event) {
    if (event) {
      event.preventDefault()
    }

    if (this._locked) {
      return
    }

    // Prevent user-interaction
    this._lock()

    this._confirmSubmit(
      () => this._submit(),
      () => this._unlock()
    )
  }

  // Private

  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }
    Util.typeCheckConfig(NAME, config, DefaultType)
    return config
  }

  _getModalElement() {
    let elem

    if (Util.isElement(this._config.modal)) {
      elem = this._config.modal[0] || this._config.modal
    } else {
      elem = document.querySelector(this._config.modal)
    }

    return elem
  }

  _getNotifyElement() {
    let elem

    if (Util.isElement(this._config.notify)) {
      elem = this._config.notify[0] || this._config.notify
    } else {
      switch (this._config.notify) {
      case 'true':
      case true:
        elem = $(this._element.querySelector(Selector.FORM_NOTIFY)).appendTo('body')[0]
        break
      default:
        elem = document.querySelector(this._config.notify)
      }
    }

    return elem
  }

  _getConfirmElements() {
    let elems

    if (Util.isElement(this._config.confirm)) {
      elems = [this._config.confirm[0] || this._config.confirm]
    } else {
      switch (this._config.confirm) {
      case 'true':
      case true:
        elems = [...$(this._element.querySelectorAll(Selector.FORM_CONFIRM)).appendTo('body')]
        break
      default:
        elems = [...document.querySelectorAll(this._config.confirm)]
      }
    }

    return elems.length ? elems : null
  }

  _confirmSubmit(accept, dismiss) {
    if (this._submitted) {
      return
    }

    Confirm.chain({ accept, dismiss, elements: this._confirmElements })
  }

  _notify() {
    if (!this._notifyElement || !this._submitted) {
      return
    }

    setTimeout(
      () => {
        $(this._notifyElement).modal({
          keyboard: false, // do not close on escape
          backdrop: 'static' // do not close on click
        })
      },
      this._config.delay
    )
  }

  _submit() {
    if (this._submitted) {
      return
    }

    this._submitted = true

    this._notify()

    // Submit the form directly w/o triggering events
    this._element.submit()
  }

  _lock() {
    if (this._locked) {
      return
    }

    this._locked = true

    this._hideModal()

    $(this._element).addClass(ClassName.LOCKED)
    $(this._element).on(
      Event.CLICK_LOCKED,
      event => event.preventDefault()
    )
  }

  _unlock() {
    if (this._submitted || !this._locked) {
      return
    }

    this._locked = false

    this._showModal()

    $(this._element).removeClass(ClassName.LOCKED)
    $(this._element).off(Event.CLICK_LOCKED)
  }

  _hideModal() {
    $(this._modalElement).modal('hide')
  }

  _showModal() {
    $(this._modalElement).modal('show')
  }

  // Static

  static _jQueryInterface(config, ...args) {
    return this.each(function () {
      let data = $(this).data(DATA_KEY)
      const _config = {
        ...Default,
        ...$(this).data(),
        ...typeof config === 'object' && config ? config : {}
      }

      if (!data) {
        data = new FormSync(this, _config)
        $(this).data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

$(document).on(Event.SUBMIT_DATA_API, Selector.DATA_FORM_SYNC, function (event) {
  FormSync._jQueryInterface.call($(this), 'submit', event)
})

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = FormSync._jQueryInterface
$.fn[NAME].Constructor = FormSync
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return FormSync._jQueryInterface
}

export default FormSync
